import { KeyboardEvent, useMemo } from 'react';
import {
  buildQueryArraySorters,
  useCreateDocumentTag,
  useDocumentTagsList,
} from '@sib/api';
import { DEFAULT_PAGE, MAXIMUM_PAGINATION } from '@sib/api/constants';
import { useUserSetupStore } from '@sib/auth';
import { createUseAppTranslation } from '@sib/i18n';
import { useNotification } from '@sib/notifications';
import { useTagsBase } from './use-tags-base';

const useDocumentsTranslation = createUseAppTranslation('documents');

export interface UseControlledDocumentTagsProps {
  organizationId?: string;
  selectedTags: string[];
  onTagsChange: (tags: string[]) => void;
}

export const useControlledDocumentTags = ({
  organizationId: organizationIdProp,
  selectedTags,
  onTagsChange,
}: UseControlledDocumentTagsProps) => {
  const organizationIdFromStore = useUserSetupStore(
    state => state.organization.id
  );
  const organizationId = organizationIdProp ?? organizationIdFromStore;
  const { t } = useDocumentsTranslation();
  const { open } = useNotification();

  const { data: tagsData } = useDocumentTagsList(organizationId, {
    page: DEFAULT_PAGE,
    size: MAXIMUM_PAGINATION,
    sort: buildQueryArraySorters([
      {
        field: 'createdAt',
        order: 'desc',
      },
    ]),
  });

  const allTags = useMemo(() => tagsData?.items || [], [tagsData]);

  const commonTagProperties = useTagsBase({
    selectedTags,
    allTagsData: allTags,
  });

  const { mutateAsync: createTagMutation, isPending: createTagPending } =
    useCreateDocumentTag({ organizationId });

  const toggleTag = (tagUuid: string) => {
    const newTagsList = selectedTags.includes(tagUuid)
      ? selectedTags.filter(uuid => uuid !== tagUuid)
      : [...selectedTags, tagUuid];

    onTagsChange(newTagsList);
  };

  const addNewTag = async () => {
    if (!commonTagProperties.validateNewTag()) return;

    try {
      const newTag = await createTagMutation({
        name: commonTagProperties.newTagName.trim(),
      });
      onTagsChange([...selectedTags, newTag.uuid]);
      commonTagProperties.resetNewTagName();
      commonTagProperties.scrollToTop();
    } catch {
      open({
        message: t('components.tagSelector.createTag.errorMessage'),
        type: 'error',
      });
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addNewTag();
    }
  };

  return {
    ...commonTagProperties,
    toggleTag,
    addNewTag,
    handleKeyDown,
    createTagPending,
  };
};
